import { useEffect, useRef, useState } from "react";
import {
  PRODUCT_TO_OFFER,
  offerTypeUsesRuntimeResolution,
} from "../constant/constant";
import cartService from "../services/cart.service";
import {
  areOfferProductsCatalogReady,
  shouldShowDummyProducts,
  generateDummyProducts,
  getDummyProductCount,
  padProductsForPreviewSlots,
  filterRealPreviewProducts,
} from "../utils/dummyProductGenerator";
import { isPopupRuntimeContentLoading, shouldSkipReactRuntimeOfferFetch } from "../utils/popup-runtime-content-loading";
import { filterProductsByOosBehavior } from "../../shared/product-offerability.js";
import {
  getOosBehavior,
  normalizeStocksCondition,
} from "../../shared/widget-market-stock.js";

/** Apply stocks OOS behavior on live storefront; editor preview may force OOS badges. */
const toLiveOfferProducts = (products, isPreview, popupData) => {
  const pool = Array.isArray(products) ? products.filter(Boolean) : [];
  const stocks = normalizeStocksCondition(popupData?.generalSettings?.condition);
  if (isPreview) {
    if (!stocks.previewOutOfStockEnabled) return pool;
    return pool.map((product) => ({
      ...product,
      weupsellOutOfStock: true,
      weupsellSoldOutMessage: stocks.previewOutOfStockMessage,
    }));
  }
  const filtered = filterProductsByOosBehavior(pool, getOosBehavior(popupData?.generalSettings?.condition));
  if (stocks.oosBehavior === "hide") return filtered;
  return filtered.map((product) =>
    product?.weupsellOutOfStock
      ? { ...product, weupsellSoldOutMessage: stocks.soldOutOfferMessage }
      : product
  );
};

/**
 * Shared offer-product list for catalog popups (recommendation + AI upsell).
 */
export default function useOfferProducts({
  section,
  popupData,
  activeStep,
  isPreviewMode,
  isMobile,
  onProductsChange,
  surface = "cart-drawer",
  fetchOfferProducts = null,
  embeddedInEditor = false,
}) {
  const [products, setProducts] = useState(() => {
    if (section?.products?.length && areOfferProductsCatalogReady(section.products)) {
      return section.products;
    }
    return [];
  });
  const [isResolvingOfferProducts, setIsResolvingOfferProducts] = useState(false);

  const productsToOfferType =
    popupData?.generalSettings?.condition?.product?.products_to_offer?.type;
  const usesRuntimeOffer = offerTypeUsesRuntimeResolution(productsToOfferType);
  const showRealtimeProduct = section?.setting?.show_realtime_product;
  const fetchOfferProductsRef = useRef(fetchOfferProducts);
  const runtimeFetchKeyRef = useRef(null);
  const runtimeFetchInFlightRef = useRef(false);

  useEffect(() => {
    fetchOfferProductsRef.current = fetchOfferProducts;
  }, [fetchOfferProducts]);

  useEffect(() => {
    const debugLiveCart =
      !isPreviewMode &&
      !embeddedInEditor &&
      (surface === "cart-drawer" || surface === "cart_drawer");
    const logOffer = (reason, extra = {}) => {
      if (!debugLiveCart) return;
      const sectionProducts = Array.isArray(section?.products)
        ? section.products
        : [];
      console.log("[WEU CartUpsell] useOfferProducts", {
        reason,
        popupId: popupData?._id,
        offerType: productsToOfferType,
        usesRuntimeOffer,
        runtimeResolved: section?.runtimeResolved === true,
        skipReactFetch: shouldSkipReactRuntimeOfferFetch({
          isPreviewMode,
          embeddedInEditor,
        }),
        sectionProductCount: sectionProducts.length,
        catalogReady: areOfferProductsCatalogReady(sectionProducts),
        ...extra,
      });
    };

    if (!isPreviewMode && usesRuntimeOffer && !section?.runtimeResolved) {
      if (section?.products?.length && areOfferProductsCatalogReady(section.products)) {
        const offerable = toLiveOfferProducts(section.products, isPreviewMode, popupData);
        logOffer("runtime-wait set from section", {
          offerableCount: offerable.length,
        });
        setProducts(offerable);
      } else {
        logOffer("runtime-wait no catalog-ready products");
      }
      return;
    }

    const productsToOffer =
      popupData?.generalSettings?.condition?.product?.products_to_offer;
    const offerType = productsToOffer?.type;
    const slotCount = getDummyProductCount(popupData, activeStep, section);
    const previewSlotCount = Math.max(1, Number(slotCount) || 1);

    if (embeddedInEditor && isPreviewMode) {
      if (shouldShowDummyProducts(offerType)) {
        setProducts(generateDummyProducts(previewSlotCount));
        return;
      }
      if (offerType === PRODUCT_TO_OFFER.SPECIFIC_PRODUCT) {
        const realProducts = filterRealPreviewProducts(section?.products);
        if (realProducts.length > 0) {
          setProducts(padProductsForPreviewSlots(realProducts, previewSlotCount));
          return;
        }
      }
      setProducts(generateDummyProducts(previewSlotCount));
      return;
    }

    if (isPreviewMode && surface === "product-page" && usesRuntimeOffer) {
      setProducts(generateDummyProducts(previewSlotCount));
      return;
    }

    if (isPreviewMode && surface === "product-page" && offerType === PRODUCT_TO_OFFER.SPECIFIC_PRODUCT) {
      const realProducts = filterRealPreviewProducts(section?.products);
      if (realProducts.length > 0) {
        setProducts(padProductsForPreviewSlots(realProducts, previewSlotCount));
        return;
      }

      setProducts([]);
      const timer = window.setTimeout(() => {
        setProducts(generateDummyProducts(previewSlotCount));
      }, 450);
      return () => window.clearTimeout(timer);
    }

    if (isPreviewMode) {
      if (shouldShowDummyProducts(offerType)) {
        setProducts(generateDummyProducts(previewSlotCount));
        return;
      }
      if (offerType === PRODUCT_TO_OFFER.SPECIFIC_PRODUCT) {
        const realProducts = filterRealPreviewProducts(section?.products);
        setProducts(
          realProducts.length === 0
            ? generateDummyProducts(previewSlotCount)
            : padProductsForPreviewSlots(realProducts, previewSlotCount)
        );
        return;
      }
      if (section?.products?.length) {
        setProducts(
          padProductsForPreviewSlots(
            filterRealPreviewProducts(section.products),
            previewSlotCount
          )
        );
        return;
      }
      setProducts(generateDummyProducts(previewSlotCount));
      return;
    }

    if (section?.products?.length && areOfferProductsCatalogReady(section.products)) {
      const offerable = toLiveOfferProducts(section.products, isPreviewMode, popupData);
      logOffer("set from section.products", {
        offerableCount: offerable.length,
        filteredOut:
          section.products.length - offerable.length,
      });
      setProducts(offerable);
      return;
    }

    if (offerType === PRODUCT_TO_OFFER.SPECIFIC_PRODUCT) {
      logOffer("specific_product clear — section not catalog-ready", {
        sectionProductCount: Array.isArray(section?.products)
          ? section.products.length
          : 0,
      });
      setProducts([]);
    }
  }, [
    activeStep,
    popupData?.generalSettings?.condition?.product?.products_to_offer,
    popupData?.generalSettings?.condition?.product?.products_to_offer_limit,
    section?.products,
    section?.runtimeResolved,
    isPreviewMode,
    usesRuntimeOffer,
    isMobile,
    surface,
    embeddedInEditor,
  ]);

  useEffect(() => {
    if (isPreviewMode) return undefined;
    if (shouldSkipReactRuntimeOfferFetch({ isPreviewMode, embeddedInEditor })) {
      return undefined;
    }
    if (productsToOfferType !== PRODUCT_TO_OFFER.SPECIFIC_PRODUCT) return undefined;
    if (typeof fetchOfferProducts !== "function") return undefined;

    const productsToOffer =
      popupData?.generalSettings?.condition?.product?.products_to_offer;
    if (!Array.isArray(productsToOffer?.value) || productsToOffer.value.length === 0) {
      return undefined;
    }

    if (section?.products?.length && areOfferProductsCatalogReady(section.products)) {
      const offerable = toLiveOfferProducts(section.products, isPreviewMode, popupData);
      if (offerable.length) {
        setProducts(offerable);
        return undefined;
      }
    }

    let cancelled = false;
    setIsResolvingOfferProducts(true);
    setProducts([]);

    (async () => {
      try {
        const productCond = popupData?.generalSettings?.condition?.product || {};
        const limit = getDummyProductCount(popupData, activeStep, section);
        const allowMultiple = productCond.allowMultipleProducts ?? true;
        const fetched = await fetchOfferProducts(
          productsToOffer,
          false,
          limit,
          allowMultiple,
          popupData,
          {
            preferCart: surface === "cart-drawer",
            surface,
          }
        );
        if (cancelled) return;

        if (!fetched) {
          setProducts([]);
          return;
        }

        const list = Array.isArray(fetched) ? fetched : [fetched];
        setProducts(toLiveOfferProducts(list, isPreviewMode, popupData));
      } catch (_) {
        if (!cancelled) {
          setProducts([]);
        }
      } finally {
        if (!cancelled) {
          setIsResolvingOfferProducts(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      setIsResolvingOfferProducts(false);
    };
  }, [
    activeStep,
    fetchOfferProducts,
    isPreviewMode,
    popupData?.generalSettings?.condition?.product,
    productsToOfferType,
    section,
  ]);

  useEffect(() => {
    if (isPreviewMode || embeddedInEditor) return undefined;
    if (shouldSkipReactRuntimeOfferFetch({ isPreviewMode, embeddedInEditor })) {
      return undefined;
    }
    if (!usesRuntimeOffer) return undefined;
    if (isPopupRuntimeContentLoading(popupData)) return undefined;
    if (section?.runtimeResolved && areOfferProductsCatalogReady(section?.products)) {
      return undefined;
    }
    if (section?.products?.length && areOfferProductsCatalogReady(section.products)) {
      const offerable = toLiveOfferProducts(section.products, isPreviewMode, popupData);
      if (offerable.length) {
        setProducts(offerable);
        return undefined;
      }
    }

    const productsToOffer =
      popupData?.generalSettings?.condition?.product?.products_to_offer;
    if (!productsToOffer?.type) return undefined;

    const fetchKey = [
      popupData?._id,
      productsToOffer.type,
      section?._id || section?.id || "default",
      activeStep,
    ].join(":");

    if (runtimeFetchKeyRef.current === fetchKey) {
      return undefined;
    }
    if (runtimeFetchInFlightRef.current) {
      return undefined;
    }

    const fetchOfferProducts = fetchOfferProductsRef.current;
    if (typeof fetchOfferProducts !== "function") return undefined;

    let cancelled = false;
    runtimeFetchInFlightRef.current = true;
    setIsResolvingOfferProducts(true);

    (async () => {
      try {
        const productCond = popupData?.generalSettings?.condition?.product || {};
        const limit = getDummyProductCount(popupData, activeStep, section);
        const allowMultiple = productCond.allowMultipleProducts ?? true;
        const fetched = await fetchOfferProducts(
          productsToOffer,
          false,
          limit,
          allowMultiple,
          popupData,
          {
            preferCart: surface === "cart-drawer",
            surface,
          }
        );
        if (cancelled) return;

        if (!fetched) {
          setProducts([]);
          return;
        }

        const list = Array.isArray(fetched) ? fetched : [fetched];
        setProducts(toLiveOfferProducts(list, isPreviewMode, popupData));
        runtimeFetchKeyRef.current = fetchKey;
      } catch (_) {
        if (!cancelled) {
          setProducts([]);
        }
      } finally {
        runtimeFetchInFlightRef.current = false;
        if (!cancelled) {
          setIsResolvingOfferProducts(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      runtimeFetchInFlightRef.current = false;
      setIsResolvingOfferProducts(false);
    };
  }, [
    activeStep,
    embeddedInEditor,
    isPreviewMode,
    popupData,
    popupData?._id,
    popupData?.generalSettings?.condition?.product?.products_to_offer?.type,
    section?._id,
    section?.id,
    section?.products,
    section?.runtimeResolved,
    usesRuntimeOffer,
  ]);

  useEffect(() => {
    if (typeof onProductsChange === "function") {
      onProductsChange(products);
    }
  }, [products, onProductsChange]);

  useEffect(() => {
    if (!showRealtimeProduct) return undefined;
    const fetchProduct = async () => {
      try {
        const handle = window.location.pathname.split("/").pop();
        const jsProductRes = await cartService.fetchProductByHandle(handle);
        setProducts([
          {
            ...jsProductRes,
            sellingPlanGroups: jsProductRes?.selling_plan_groups,
          },
        ]);
      } catch (_) {
        /* preview / storefront may lack product context */
      }
    };
    fetchProduct();
  }, [showRealtimeProduct]);

  return { products, isResolvingOfferProducts };
}
